import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, CardContent, TextField, Button, Typography, MenuItem, Switch,
  FormControlLabel, Checkbox, Radio, IconButton, Stack, Divider, Alert, Box, CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import QuizLayout from './QuizLayout';
import AdminGate from './AdminGate';
import { quizService } from './quizService';
import { emptyQuiz, emptyQuestion, setCorrect, normalizePositions, narrowCorrectForType } from './editorModel';

const REVIEW_MODES = [
  { value: 'all', label: 'Review every question' },
  { value: 'wrong_only', label: 'Review wrong answers only' },
  { value: 'none', label: 'No review (score/pass only)' },
];
const Q_TYPES = [
  { value: 'single', label: 'Single choice' },
  { value: 'true_false', label: 'True / False' },
  { value: 'multi', label: 'Select all that apply' },
];

function EditorInner({ pass }) {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const isNew = quizId === 'new';
  const [quiz, setQuiz] = useState(isNew ? emptyQuiz() : null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) {
      quizService.adminGet(quizId, pass)
        .then((q) => setQuiz({ ...emptyQuiz(), ...q }))
        .catch((e) => setError(e.message));
    }
  }, [quizId, pass, isNew]);

  if (error && !quiz) return <QuizLayout title="Edit Quiz"><Alert severity="error">{error}</Alert></QuizLayout>;
  if (!quiz) return <QuizLayout title="Edit Quiz"><Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box></QuizLayout>;

  const setField = (field, value) => setQuiz((q) => ({ ...q, [field]: value }));

  const updateQuestion = (qi, updater) => setQuiz((q) => ({
    ...q, questions: q.questions.map((qq, i) => (i === qi ? updater(qq) : qq)),
  }));

  const addQuestion = () => setQuiz((q) => ({ ...q, questions: [...q.questions, emptyQuestion('single')] }));
  const removeQuestion = (qi) => setQuiz((q) => ({ ...q, questions: q.questions.filter((_, i) => i !== qi) }));

  const changeType = (qi, type) => updateQuestion(qi, (qq) => {
    // reset options when switching to/from true_false to keep them valid
    if (type === 'true_false') return { ...emptyQuestion('true_false'), prompt: qq.prompt, explanation: qq.explanation, points: qq.points };
    return narrowCorrectForType(qq, type);
  });

  const addOption = (qi) => updateQuestion(qi, (qq) => ({ ...qq, options: [...qq.options, { text: '', is_correct: false, position: qq.options.length }] }));
  const removeOption = (qi, oi) => updateQuestion(qi, (qq) => ({ ...qq, options: qq.options.filter((_, i) => i !== oi) }));
  const setOptionText = (qi, oi, text) => updateQuestion(qi, (qq) => ({ ...qq, options: qq.options.map((o, i) => (i === oi ? { ...o, text } : o)) }));
  const setOptionCorrect = (qi, oi, checked) => updateQuestion(qi, (qq) => setCorrect(qq, oi, checked));

  const save = async () => {
    setSaving(true); setError('');
    try {
      const payload = normalizePositions(quiz);
      const result = isNew
        ? await quizService.adminCreate(payload, pass)
        : await quizService.adminUpdate(quizId, payload, pass);
      navigate('/admin/quizzes');
      return result;
    } catch (e) {
      setError(e.message); setSaving(false);
    }
  };

  return (
    <QuizLayout title={isNew ? 'New Quiz' : 'Edit Quiz'}
      action={(
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/quizzes')} data-testid="view-quizzes">Quizzes</Button>
          <Button variant="outlined" onClick={() => navigate('/admin/quizzes')} data-testid="manage-quizzes">Manage</Button>
          <Button variant="contained" onClick={save} disabled={saving || !quiz.title.trim()} data-testid="save-quiz">Save</Button>
        </Stack>
      )}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}><CardContent>
        <TextField fullWidth label="Title" value={quiz.title} onChange={(e) => setField('title', e.target.value)} sx={{ mb: 2 }} inputProps={{ 'data-testid': 'quiz-title' }} />
        <TextField fullWidth label="Description" value={quiz.description || ''} onChange={(e) => setField('description', e.target.value)} sx={{ mb: 2 }} />
        <TextField fullWidth label="Category" value={quiz.category || ''} onChange={(e) => setField('category', e.target.value)} sx={{ mb: 2 }} />
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Result display</Typography>
        <FormControlLabel control={<Switch checked={quiz.show_score} onChange={(e) => setField('show_score', e.target.checked)} />} label="Show score %" />
        <FormControlLabel control={<Switch checked={quiz.show_pass_fail} onChange={(e) => setField('show_pass_fail', e.target.checked)} />} label="Show pass/fail" />
        {quiz.show_pass_fail && (
          <TextField type="number" label="Passing score %" value={quiz.passing_score ?? 0}
            onChange={(e) => setField('passing_score', Number(e.target.value))} sx={{ ml: 2, width: 160 }} inputProps={{ min: 0, max: 100 }} />
        )}
        <TextField select fullWidth label="Review mode" value={quiz.review_mode}
          onChange={(e) => setField('review_mode', e.target.value)} sx={{ mt: 2 }}>
          {REVIEW_MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>
        <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={quiz.is_published} onChange={(e) => setField('is_published', e.target.checked)} />} label="Published (visible to coordinators)" />
      </CardContent></Card>

      {quiz.questions.map((q, qi) => (
        <Card key={qi} sx={{ mb: 2 }}><CardContent>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Question {qi + 1}</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <TextField type="number" size="small" label="Points" value={q.points}
              onChange={(e) => updateQuestion(qi, (qq) => ({ ...qq, points: Number(e.target.value) || 1 }))}
              sx={{ width: 100, mr: 1 }} inputProps={{ min: 1 }} />
            <TextField select size="small" label="Type" value={q.type} onChange={(e) => changeType(qi, e.target.value)} sx={{ width: 200, mr: 1 }}>
              {Q_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <IconButton onClick={() => removeQuestion(qi)} title="Delete question"><DeleteIcon /></IconButton>
          </Stack>
          <TextField fullWidth label="Prompt" value={q.prompt} onChange={(e) => updateQuestion(qi, (qq) => ({ ...qq, prompt: e.target.value }))} sx={{ mb: 2 }} />
          <Typography variant="caption" color="text.secondary">Options {q.type === 'multi' ? '(check all correct)' : '(select the correct one)'}</Typography>
          {q.options.map((o, oi) => (
            <Stack key={oi} direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              {q.type === 'multi'
                ? <Checkbox checked={o.is_correct} onChange={(e) => setOptionCorrect(qi, oi, e.target.checked)} />
                : <Radio checked={o.is_correct} onChange={(e) => setOptionCorrect(qi, oi, e.target.checked)} />}
              <TextField fullWidth size="small" placeholder={`Option ${oi + 1}`} value={o.text}
                onChange={(e) => setOptionText(qi, oi, e.target.value)}
                disabled={q.type === 'true_false'} />
              {q.type !== 'true_false' && <IconButton size="small" onClick={() => removeOption(qi, oi)}><DeleteIcon fontSize="small" /></IconButton>}
            </Stack>
          ))}
          {q.type !== 'true_false' && <Button size="small" startIcon={<AddIcon />} onClick={() => addOption(qi)}>Add option</Button>}
          <Divider sx={{ my: 2 }} />
          <TextField fullWidth multiline minRows={2} label="Explanation (shown in review)" value={q.explanation || ''}
            onChange={(e) => updateQuestion(qi, (qq) => ({ ...qq, explanation: e.target.value }))} />
        </CardContent></Card>
      ))}

      <Button startIcon={<AddIcon />} onClick={addQuestion} data-testid="add-question">Add question</Button>
    </QuizLayout>
  );
}

export default function AdminQuizEditor() {
  return <AdminGate>{(pass) => <EditorInner pass={pass} />}</AdminGate>;
}
